/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-empty-function */
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { BehaviorSubject, ReplaySubject, Subject, Subscription, takeUntil } from 'rxjs';
import { PropertyService } from '../../services';
import { createPropertyControl } from '../properties/abstract-property-input.component';

export interface PropertySetInputValue {
  propertyValues: Record<string, unknown>;
  dimensionValues: Record<string, unknown>;
}

export type OrderedSetAssignment =
  | {
      kind: 'PROPERTY';
      order: number;
      propertyAssignment: PropertySet.PropertyAssignment;
    }
  | {
      kind: 'DIMENSION';
      order: number;
      dimensionAssignment: PropertySet.DimensionAssignment;
    };

export function getOrderedAssignments(set: PropertySet.Model): OrderedSetAssignment[] {
  return [
    ...(set.properties ?? []).map<OrderedSetAssignment>(propertyAssignment => ({
      kind: 'PROPERTY',
      order: propertyAssignment.order,
      propertyAssignment,
    })),
    ...(set.dimensions ?? []).map<OrderedSetAssignment>(dimensionAssignment => ({
      kind: 'DIMENSION',
      order: dimensionAssignment.order,
      dimensionAssignment,
    })),
  ].sort((left, right) => left.order - right.order);
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-property-set-input',
  templateUrl: './property-set-input.component.html',
  standalone: false,
  hostDirectives: [CdkScrollable],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PropertySetInputComponent,
    },
    { provide: NG_VALIDATORS, useExisting: PropertySetInputComponent, multi: true },
  ],
})
export class PropertySetInputComponent implements ControlValueAccessor, OnInit, OnDestroy, Validator {
  constructor(
    private propertySvc: PropertyService,
    private cdr: ChangeDetectorRef
  ) {}

  @PropertyListener('setId') setId$ = new BehaviorSubject<string | null>(null);
  @Input() setId!: string;

  @Input() showHidden = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() extraData?: Record<string, any>;

  form!: FormGroup;

  get valid() {
    return this.form?.valid ?? false;
  }

  private _value: PropertySetInputValue = { propertyValues: {}, dimensionValues: {} };
  get value() {
    return this._value;
  }
  @Input() set value(value: PropertySetInputValue | Record<string, unknown>) {
    this._value = this._normalizeValue(value);
    this.form?.patchValue(this._value);
  }

  @Input() disabled = false;

  set!: PropertySet.Model;
  orderedAssignments: OrderedSetAssignment[] = [];

  protected onDestroy$ = new Subject<void>();
  protected touched$ = new ReplaySubject<void>(1);
  private formValueChangesSub?: Subscription;

  onChange = (_value: PropertySetInputValue) => {};
  onTouched = () => {};

  validate(): ValidationErrors | null {
    if (this.form) return extractErrors(this.form);
    return null;
  }

  writeValue(value: PropertySetInputValue | Record<string, unknown> | null): void {
    this._value = this._normalizeValue(value ?? {});
    this.form?.patchValue(this._value);
  }

  registerOnChange(fn: (value: PropertySetInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
    this.touched$.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.onTouched());
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  async ngOnInit() {
    this.setId$.pipe(takeUntil(this.onDestroy$)).subscribe(setId => {
      void this.loadPropertySet(setId);
    });
  }

  ngOnDestroy() {
    this.formValueChangesSub?.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.touched$.complete();
  }

  get propertyValuesForm(): FormGroup {
    return this.form.get('propertyValues') as FormGroup;
  }

  get dimensionValuesForm(): FormGroup {
    return this.form.get('dimensionValues') as FormGroup;
  }

  private async loadPropertySet(setId: string | null) {
    if (!setId) {
      this.set = undefined as never;
      this.form = undefined as never;
      this.orderedAssignments = [];
      this.formValueChangesSub?.unsubscribe();
      this.detectChangesSafe();
      return;
    }

    this.set = await this.propertySvc.retrievePropertySet(setId);

    this.set.properties = this.set.properties?.sort((a, b) => a.order - b.order);
    this.set.dimensions = this.set.dimensions?.sort((a, b) => a.order - b.order);
    this.orderedAssignments = getOrderedAssignments(this.set);

    const propertyControls: Record<string, FormControl> = {};
    for (const assignment of this.set.properties ?? []) {
      const property = assignment.property!;
      propertyControls[property.valueKey] = createPropertyControl(property, assignment.required);
    }

    const dimensionControls: Record<string, FormControl> = {};
    for (const assignment of this.set.dimensions ?? []) {
      if (assignment.dimension) {
        dimensionControls[assignment.dimensionId] = new FormControl<string | number | null>(
          null,
          assignment.required ? [Validators.required] : []
        );
      }
    }

    this.form = new FormGroup({
      propertyValues: new FormGroup(propertyControls),
      dimensionValues: new FormGroup(dimensionControls),
    });

    this.form.patchValue(this._value);
    this.formValueChangesSub?.unsubscribe();
    this.formValueChangesSub = this.form.valueChanges.subscribe(value => this.onChange(value));

    if (this.disabled) this.form.disable();
    else this.form.enable();

    this.detectChangesSafe();
  }

  /** The set resolves after an await, so run CD or the fields never paint. */
  private detectChangesSafe() {
    try {
      this.cdr.detectChanges();
    } catch {
      // View may already be destroyed if the host closed while the set was loading.
    }
  }

  private _normalizeValue(value: PropertySetInputValue | Record<string, unknown> | null): PropertySetInputValue {
    if (!value) return { propertyValues: {}, dimensionValues: {} };
    if ('propertyValues' in value || 'dimensionValues' in value) {
      return {
        propertyValues: ((value as PropertySetInputValue).propertyValues as Record<string, unknown>) ?? {},
        dimensionValues: ((value as PropertySetInputValue).dimensionValues as Record<string, unknown>) ?? {},
      };
    }
    // Legacy flat format — treat all keys as property values
    return { propertyValues: value as Record<string, unknown>, dimensionValues: {} };
  }
}
